import React from 'react';
import { useTranslation } from 'react-i18next';
import { PriceScale, decimalToNumber } from '../api/asset/assetApiTypes';
import { formatStoredTimeOfDay } from '../utils/dateTimeUtils';
import { spaceOf } from '../utils/useCases';
import { netFromGross } from '../utils/netGross';
import { usePriceEntryMode, usePriceListTaxRate } from '../hooks/usePriceEntry';
import { formatRatePercent } from './tax/formatRatePercent';
import { useGetCustomerClassesQuery } from '../api/organization/organizationApiSlice';

/**
 * Read-only tariff-row table — extracted from `PricingSection`'s view mode so
 * the Services price view (service-configuration-area plan, phase 1b) renders
 * rows exactly like the asset detail page instead of duplicating the markup.
 * Both consumers must show the same row the same way: this table is how an
 * operator sees what actually prices a booking.
 */
interface PriceScaleTableProps {
    priceScales: PriceScale[] | undefined;
    /** Its space properties decide the wording: a night-booked use case counts
     *  nights, a use case with rate units names the half-day row. */
    useCase?: string;
    /** The price list, for its VAT rate when the tenant enters prices net (#2967). */
    priceCategoryId?: string | null;
}

/** Net, VAT and gross of one stored gross price — the invoice side's split. */
const SplitPriceCells = ({ gross, ratePercent, formatPrice }: Readonly<{
    gross: number;
    ratePercent: number;
    formatPrice: (value: number) => string;
}>) => {
    const net = netFromGross(gross, ratePercent);
    return (
        <>
            <td className={'py-2 pr-4 text-foreground text-right whitespace-nowrap'}>{formatPrice(net)}</td>
            <td className={'hidden sm:table-cell py-2 pr-4 text-foreground text-right whitespace-nowrap'}>
                {`${formatRatePercent(ratePercent)} %`}
            </td>
            <td className={'hidden sm:table-cell py-2 pr-4 text-foreground text-right whitespace-nowrap'}>
                {formatPrice(Math.round((gross - net) * 100) / 100)}
            </td>
            <td className={'py-2 text-foreground font-medium text-right whitespace-nowrap'}>{formatPrice(gross)}</td>
        </>
    );
};

const PriceScaleTable: React.FC<PriceScaleTableProps> = ({ priceScales, useCase, priceCategoryId }) => {
    const { t, i18n } = useTranslation();
    // Stored prices are GROSS. Wherever the list has one VAT rate, the table
    // shows net, rate, VAT and the customer's gross — the same split the
    // editor and the invoice make (`utils/netGross.ts`) — so a bare price can
    // never be read as the wrong one (PO, staging 2026-10-21).
    const priceEntry = usePriceEntryMode();
    const rate = usePriceListTaxRate(priceCategoryId);
    const splitRate = rate.ratePercent;
    // #1808: class names for class rates. A legacy tenant's endpoint answers
    // 409 and leaves this empty — it has no class rates either.
    const { data: customerClasses = [] } = useGetCustomerClassesQuery();
    const classNames = new Map(customerClasses.map((cls) => [cls.id, cls.name]));
    const locale = i18n.language.startsWith('de') ? 'de-DE' : 'en-US';
    const space = spaceOf(useCase);
    const countsNights = space?.timeUnit === 'night';

    const formatPrice = (val: number): string =>
        val.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EUR';

    const formatDuration = (minutes: number): string => {
        const nights = minutes / 1440;
        if (Number.isInteger(nights) && nights > 0) {
            if (countsNights) {
                return nights === 1
                    ? `${nights} ${t('asset_detail.fields.night')}`
                    : `${nights} ${t('asset_detail.fields.nights')}`;
            }
            return nights === 1
                ? `${nights} ${t('asset_detail.fields.day')}`
                : `${nights} ${t('asset_detail.fields.days')}`;
        }
        const hours = minutes / 60;
        if (Number.isInteger(hours) && hours > 0) {
            return hours === 1
                ? `${hours} ${t('asset_detail.fields.hour')}`
                : `${hours} ${t('asset_detail.fields.hours')}`;
        }
        return `${minutes} min`;
    };

    const dayTypeLabels: Record<string, string> = {
        weekday: t('asset_detail.fields.weekday'),
        weekend: t('asset_detail.fields.weekend'),
    };

    const notApplicable = t('common.not_applicable');

    // `days` is overloaded in the schema: 'weekday' / 'weekend' for venues, a
    // DAY COUNT for full-day scales, and a comma-separated weekday list for the
    // SUP/rental tariff rows. Each meaning belongs in a different column, so
    // they are separated here rather than collapsed into one string — a SUP
    // group has several rows of the same duration whose ONLY difference is the
    // weekday and time window, and rendering just the duration made them
    // indistinguishable (four "1 Stunde" rows at four different prices).
    const isFullDayCount = (scale: PriceScale): boolean =>
        scale.isFullDay && !!scale.days && Number.isFinite(Number(scale.days));

    const formatScaleDuration = (scale: PriceScale): string => {
        if (isFullDayCount(scale)) {
            const dayCount = Number(scale.days) || 1;
            return countsNights
                ? `${dayCount} ${dayCount === 1 ? t('asset_detail.fields.night') : t('asset_detail.fields.nights')}`
                : `${dayCount} ${dayCount === 1 ? t('asset_detail.fields.day') : t('asset_detail.fields.days')}`;
        }
        if (scale.isFullDay) {
            return countsNights
                ? `1 ${t('asset_detail.fields.night')}`
                : `1 ${t('asset_detail.fields.day')}`;
        }
        // #1713: on a venue sheet the 720 row is the anchored HALF-DAY marker,
        // not a 12-hour tier — naming it "12 Stunden" would misstate the rule
        // (11:00–15:00 never prices as half-day). Rental/storage keep their
        // literal duration tiers.
        if (!countsNights && space?.rateUnits === true
            && scale.durationInMinutes === 720) {
            return t('asset_detail.fields.half_day');
        }
        if (scale.durationInMinutes) {
            return formatDuration(scale.durationInMinutes);
        }
        return notApplicable;
    };

    // Localized short weekday names, derived rather than translated: a fixed
    // week (2024-01-01 was a Monday) run through Intl, so this follows the UI
    // language without another seven i18n keys per locale.
    const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const shortWeekdayNames: Record<string, string> = Object.fromEntries(
        WEEKDAY_KEYS.map((key, i) => [
            key,
            new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' })
                .format(new Date(Date.UTC(2024, 0, 1 + i))),
        ]),
    );

    const formatDayScope = (scale: PriceScale): string => {
        // A day COUNT is a duration, not a weekday scope — already rendered
        // in the duration column.
        if (isFullDayCount(scale)) return notApplicable;
        if (!scale.days) return notApplicable;
        if (dayTypeLabels[scale.days]) return dayTypeLabels[scale.days];
        const tokens = scale.days
            .split(',')
            .map((d) => d.trim().toLowerCase())
            .filter((d) => d.length > 0);
        if (tokens.length === 0) return notApplicable;
        // An unrecognized token is shown verbatim rather than dropped: a value
        // the pricing engine cannot match is exactly what an operator needs to
        // see on this screen.
        return tokens.map((token) => shortWeekdayNames[token] ?? token).join(', ');
    };

    // Not 'HH:MM:SS' — Prisma serializes the `Time(6)` column as a full ISO
    // instant, so #1898 rendered '1970-01-01T13:00:00.000Z' verbatim into the
    // cell. `formatStoredTimeOfDay` takes the wall-clock digits out of it; the
    // column holds local wall-clock since ADR-0019, so nothing is converted and
    // the boundary shown does not move with the season.
    const formatTimeWindow = (scale: PriceScale): string => {
        const from = formatStoredTimeOfDay(scale.startTime);
        const to = formatStoredTimeOfDay(scale.endTime);
        if (from && to) return `${from} – ${to}`;
        if (from) return `${t('asset_detail.fields.from')} ${from}`;
        if (to) return `${t('asset_detail.fields.until')} ${to}`;
        return notApplicable;
    };

    // Sorted by what the rows MEAN, not by price: duration first, then the
    // time window, then price. A price-ascending list interleaved the tariff
    // rows of different durations, so the variants of one duration — which is
    // what an operator compares — were scattered down the table.
    const sortedScales = [...(priceScales ?? [])].sort((a, b) => {
        const durationDiff = (a.durationInMinutes ?? 0) - (b.durationInMinutes ?? 0);
        if (durationDiff !== 0) return durationDiff;
        const startDiff = (a.startTime ?? '').localeCompare(b.startTime ?? '');
        if (startDiff !== 0) return startDiff;
        return (decimalToNumber(a.price) ?? 0) - (decimalToNumber(b.price) ?? 0);
    });
    if (sortedScales.length === 0) return null;

    // Only shown where they carry information. Venue and apartment scales have
    // neither, and two columns of "n.z." would be pure noise there.
    const showDayScope = sortedScales.some((s) => !!s.days && !isFullDayCount(s));
    const showTimeWindow = sortedScales.some((s) => !!s.startTime || !!s.endTime);

    return (
        <div className={'mt-4 overflow-x-auto'}>
            <table className={'w-full text-sm'}>
                <thead>
                    <tr className={'border-b border-foreground/10'}>
                        <th className={'text-left py-2 pr-4 text-foreground/60 font-medium whitespace-nowrap'}>
                            {t('asset_detail.fields.duration')}
                        </th>
                        {showDayScope && (
                            <th className={'text-left py-2 pr-4 text-foreground/60 font-medium whitespace-nowrap'}>
                                {t('asset_detail.fields.day_scope')}
                            </th>
                        )}
                        {showTimeWindow && (
                            <th className={'text-left py-2 pr-4 text-foreground/60 font-medium whitespace-nowrap'}>
                                {t('asset_detail.fields.time_window')}
                            </th>
                        )}
                        {splitRate === null ? (
                            <th className={'text-right py-2 text-foreground/60 font-medium whitespace-nowrap'}>
                                {t('price_entry.table.gross_price')}
                            </th>
                        ) : (
                            <>
                                <th className={'text-right py-2 pr-4 text-foreground/60 font-medium whitespace-nowrap'}>
                                    {t('price_entry.table.net')}
                                </th>
                                {/* On a phone the rate and the VAT amount give way, so
                                    net and the customer's gross both fit; the rate
                                    moves into the gross header (PO, 2026-10-23). */}
                                <th className={'hidden sm:table-cell text-right py-2 pr-4 text-foreground/60 font-medium whitespace-nowrap'}>
                                    {t('price_entry.table.rate')}
                                </th>
                                <th className={'hidden sm:table-cell text-right py-2 pr-4 text-foreground/60 font-medium whitespace-nowrap'}>
                                    {t('price_entry.table.vat')}
                                </th>
                                <th className={'text-right py-2 text-foreground/60 font-medium whitespace-nowrap'}>
                                    {t('price_entry.table.gross')}
                                    <span className={'sm:hidden'}>{` (${formatRatePercent(splitRate)} %)`}</span>
                                </th>
                            </>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {sortedScales.map((scale) => (
                        <tr key={scale.id} className={'border-b border-foreground/5'}>
                            <td className={'py-2 pr-4 text-foreground whitespace-nowrap'}>
                                {formatScaleDuration(scale)}
                                {/* A class rate sits beside the general row of the same
                                    scope; without its class it reads as a duplicate
                                    with a second price (PO, staging 2026-10-23). */}
                                {scale.customerClassId && (
                                    <span className={'block text-xs text-foreground/60'}>
                                        {classNames.get(scale.customerClassId) ?? t('common.missing')}
                                    </span>
                                )}
                            </td>
                            {showDayScope && (
                                <td className={'py-2 pr-4 text-foreground/80 whitespace-nowrap'}>
                                    {formatDayScope(scale)}
                                </td>
                            )}
                            {showTimeWindow && (
                                <td className={'py-2 pr-4 text-foreground/80 whitespace-nowrap'}>
                                    {formatTimeWindow(scale)}
                                </td>
                            )}
                            {splitRate === null ? (
                                <td className={'py-2 text-foreground font-medium text-right whitespace-nowrap'}>
                                    {formatPrice(decimalToNumber(scale.price) ?? 0)}
                                </td>
                            ) : (
                                <SplitPriceCells
                                    gross={decimalToNumber(scale.price) ?? 0}
                                    ratePercent={splitRate}
                                    formatPrice={formatPrice}
                                />
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
            {priceEntry === 'net' && rate.block && (
                <p className={'mt-2 text-xs text-foreground/60'}>{t(`price_entry.blocked.${rate.block}`)}</p>
            )}
        </div>
    );
};

export default PriceScaleTable;
